import { CaptureConsole, Dedupe, ExtraErrorData } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

import { CI, HOST_DOMAIN, SENTRY_HOST, VERSION } from '@webapp/common/conf';
import { isBrowser, isProd } from '@webapp/common/lib/const';
import { getCookie, setCookie } from '@webapp/common/lib/cookies';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';

const isCI = !!CI;

const knownErrors = [
    'ResizeObserver loop limit exceeded', // https://github.com/souporserious/react-measure/issues/104,
    'ResizeObserver loop completed with undelivered notifications', // https://github.com/ckeditor/ckeditor5/issues/7371,
    "'destroy'", // https://github.com/ckeditor/ckeditor5-react/issues/241,
    'Non-Error exception captured with keys', // sentry got not Error object
    'Socket hung up', // local dev-server
    'status code 404',
    'Loading chunk',
    'Loading CSS chunk',
    'Failed to fetch',
    'ChunkLoadError',
    'ошибка сети',
    'Load failed',
    'is not valid JSON', // console-шум parseJSON из старых данных
    'JSON Parse error',
    'fetch failed', // сетевая ошибка fetch (Node-формулировка, ср. 'Failed to fetch')
    'attempted to hard navigate', // Next.js invariant при навигации на тот же URL
    'Cache.addAll', // browser/PWA-кэш: дубликаты и сетевые сбои
    'addAll cannot store',
    'CacheStorage',
    'Failed writing data to the file system', // file system / кэш браузера
    'postEvent: Method not found', // WebView native bridge
    'Non-Error promise rejection captured',
    'Blocked a frame with origin', // cross-origin iframe
    'element not found: condition', // шум редактора условий (см. question.tsx)
    'Requested and resolved page mismatch', // сканеры/LFI-пробы: внутренний guard Next на path traversal (FRONT-23A8/269H и родственные)
    'ошибка ответа сервера', // non-JSON ответ API, отдаётся через reject (см. fetch.ts)
    'webkit.messageHandlers', // WebView iOS: нет native-бриджа
    'Java object is gone', // WebView Android: native-бридж умер
    'The operation is insecure', // Firefox: environment SecurityError
];

const withKnownError = (msg: string): boolean => knownErrors.some((e) => msg && msg.includes(e));

// --- «Только наши скрипты» -------------------------------------------------
// Не отправляем события, которые упали в стороннем/инжектированном коде (боты,
// расширения браузера, WebView-мосты). Ориентир — место падения: Sentry хранит фреймы
// от внешнего к глубинному (последний — где брошено исключение), а нативные вызовы
// (JSON.parse и т.п.) идут как '<anonymous>' без строки. Наш код всегда в файлах
// (бандлы/sourcemapped-кадры), а инжектированные eval-скрипты — '<anonymous>' со
// строкой:колонкой (FRONT-1ZFP/1ZJH и родственные).
const OUR_HOST_RE = /(^|\.)(testograf|ttgraf)\.ru$/i;
// уникальные префиксы наших сборок — работают и на кастомных доменах опросов
const OUR_SCRIPT_PATH_RE = /(?:survey-assets|landing-assets|assets-public-results)\//;
const FOREIGN_FRAME_RE = /^(?:(?:chrome|moz|safari|safari-web)-extension|webkit-masked-url|file):/i;
const FOREIGN_STACK_RE = /(?:chrome|moz|safari|safari-web)-extension:\/\/|webkit-masked-url:/i;
const EVAL_FRAME_RE = /<anonymous>:\d+:\d+/;

const isOurScript = (file: string): boolean => {
    if (FOREIGN_FRAME_RE.test(file)) return false;

    if (/^(https?:)?\/\//i.test(file)) {
        try {
            return OUR_HOST_RE.test(new URL(file, 'https://ttgraf.ru').hostname) || OUR_SCRIPT_PATH_RE.test(file);
        } catch {
            return false;
        }
    }

    // относительные пути / webpack-кадры / sourcemapped-модули — наши бандлы
    return true;
};

const isForeignCrashSite = (frames: Array<{ filename?: string; lineno?: number }> | undefined): boolean => {
    if (!frames?.length) return false;

    for (let i = frames.length - 1; i >= 0; i--) {
        const { filename, lineno } = frames[i];
        const file = filename || '';

        // нативный вызов — смотрим следующий (более внешний) кадр
        if (!file || file === '[native code]' || (file === '<anonymous>' && !lineno)) {
            continue;
        }

        // анонимный кадр со строкой:колонкой или чужая схема — инжект/расширение
        if (file === '<anonymous>' || FOREIGN_FRAME_RE.test(file)) {
            return true;
        }

        return !isOurScript(file);
    }

    return false;
};

const isForeignScriptEvent = (event: Sentry.Event): boolean => {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames?.length) {
        return isForeignCrashSite(frames);
    }

    // console-события CaptureConsole: аргументы в extra.arguments; у ошибок из чужих
    // скриптов там объект со строковым стеком (FRONT-1ZFP/1ZJH)
    const args = (event.extra as { arguments?: Array<unknown> } | undefined)?.arguments;
    if (Array.isArray(args)) {
        return args.some((arg) => {
            const stack = arg && typeof arg === 'object' ? (arg as { stack?: unknown }).stack : null;
            return typeof stack === 'string' && (FOREIGN_STACK_RE.test(stack) || EVAL_FRAME_RE.test(stack));
        });
    }

    return false;
};

// Окружение для фильтра environment в Sentry UI: prod | dev | test1..test5.
// В браузере стенд определяем по хосту (включая поддомены опросов: nps-x.test3.ttgraf.ru),
// на SSR — по домену сборки CI (HOST_DOMAIN): testograf.ru -> prod, ttgraf.ru -> dev,
// testN.ttgraf.ru -> testN.
const TEST_STAND_RE = /(^|\.)test(\d+)\.ttgraf\.ru$/i;
const TTGRAF_RE = /(^|\.)ttgraf\.ru$/i;

export const getSentryEnvironment = (host?: string): string => {
    const target = host || (typeof window !== 'undefined' ? window.location.hostname : '') || HOST_DOMAIN;
    const testStand = target.match(TEST_STAND_RE);

    if (testStand) {
        return `test${testStand[2]}`;
    }

    return TTGRAF_RE.test(target) ? 'dev' : 'prod';
};

// Стабильный анонимный id для user-метрик release health (adoption и crash-free
// users). Переиспользуем device fingerprint (tt_fp): он и так создаётся на
// бутстрапе (common/lib/api.ts) и уходит в бэкенд вместе с ответами опросов.
// Cookie — запасное хранилище для контекстов, где localStorage недоступен
// (встроенные опросы в iframe, приватный режим): без общего id Sentry считал бы
// каждый заход новым пользователем и завышал user-adoption.
const SENTRY_USER_COOKIE = 'tt_uid';

const getSentryUserId = (): string => {
    try {
        const fingerprint = LocalStorage.get<string>(LocalStorageKeys.DEVICE_FINGERPRINT);

        if (fingerprint) return fingerprint;

        const fromCookie = getCookie(SENTRY_USER_COOKIE);

        if (fromCookie) {
            LocalStorage.set(LocalStorageKeys.DEVICE_FINGERPRINT, fromCookie);

            return fromCookie;
        }

        const id = nanoid();

        LocalStorage.set(LocalStorageKeys.DEVICE_FINGERPRINT, id);
        setCookie(SENTRY_USER_COOKIE, id, {
            path: '/',
            // больше 400 дней Chrome всё равно не хранит JS-куки
            expires: dayjs().add(400, 'day').toDate(),
        });

        return id;
    } catch {
        // хранилища могут быть недоступны (экзотические встройки): Sentry всё
        // равно должен инициализироваться, пусть id будет на один заход.
        return nanoid();
    }
};

const init = (_module: string): void => {
    if (!isProd || !isCI) return;

    const dsn = `https://82b6f05462dd4820a62cb105d6ed34fd@${SENTRY_HOST}/4`;

    Sentry.init({
        dsn,
        release: VERSION,
        environment: getSentryEnvironment(),
        // release health: adoption и crash-free по релизам. Добавляет ~1 маленькую
        // session-конверту на загрузку страницы; сессии не семплируются
        // (sessionsSampleRate есть только в SDK v8), а сниженный tracesSampleRate
        // компенсирует поток с запасом.
        autoSessionTracking: true,
        // user-id кладём в initialScope (а не setUser после init): initialScope
        // применяется до старта session-трекинга и попадает даже в первую
        // сессию. Нужен для user-adoption и crash-free users.
        initialScope: isBrowser ? { user: { id: getSentryUserId() } } : undefined,
        // TODO check server mode
        integrations: [
            new Integrations.BrowserTracing(),
            new CaptureConsole({ levels: ['error'] }),
            new Dedupe(),
            new ExtraErrorData({
                depth: 3,
            }),
        ],
        beforeSend(event, hint) {
            try {
                const originalException = hint?.originalException as Error | undefined;
                const exceptionValue = event?.exception?.values?.[0]?.value;
                // console-события (CaptureConsole) не всегда имеют event.message/originalException —
                // смотрим также в exception.values, иначе фильтр известных ошибок их пропускает.
                const msg = event?.message || exceptionValue || originalException?.message;

                if (withKnownError(msg)) {
                    return null;
                }

                // ошибки сторонних/инжектированных скриптов не отправляем — только наши бандлы
                if (isForeignScriptEvent(event)) {
                    return null;
                }
            } catch (e) {
                // beforeSend сам не должен ронять отправку событий (ср. FRONT-Q7N).
                console.warn('sentry beforeSend failed', e);
            }

            return event;
        },
        // Снижено с 1.0: 100% трейсинг давал ~200k транзакций/сутки (замер
        // 14.10), 0.2 с запасом компенсирует добавленные сессии и разгружает
        // self-hosted Sentry.
        tracesSampleRate: 0.2,
    });

    Sentry.setTag('is_canary', getCookie('is_canary') === 'always');
};

// eslint-disable-next-line import/no-default-export
export default init;
